import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  EnvelopeSimple, 
  PaperPlaneRight,
  MagnifyingGlass,
  Plus,
  ArrowLeft,
  User,
  Clock,
  Ticket,
  Tag,
  Warning,
  CaretRight,
  CheckCircle,
  Trash
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { getEmailThreads, getEmailThread, sendEmail, createIssue, deleteEmailThread, getOrganization, Organization } from '../lib/api';

const CATEGORIES = [
  { value: 'plumbing', label: 'VVS' },
  { value: 'electrical', label: 'El' },
  { value: 'heating', label: 'Värme' },
  { value: 'ventilation', label: 'Ventilation' },
  { value: 'appliances', label: 'Vitvaror' },
  { value: 'locks', label: 'Lås' },
  { value: 'windows', label: 'Fönster/Glas' },
  { value: 'outdoor', label: 'Utemiljö' },
  { value: 'cleaning', label: 'Städning' },
  { value: 'pest', label: 'Skadedjur' },
  { value: 'elevator', label: 'Hiss' },
  { value: 'other', label: 'Övrigt' }
];

// Type definitions
interface EmailMessage {
  id: string;
  direction: string;
  body?: string;
  from_email?: string;
  to_email?: string;
  created_at: string;
}

interface EmailThread {
  id: string;
  subject?: string;
  participant_email?: string;
  participant_name?: string;
  participants?: string[];
  issue_id?: string;
  issue?: {
    issue_number?: string;
  };
  reference?: string;
  messages?: EmailMessage[];
  last_message_at?: string;
  last_message_preview?: string;
  message_count?: number;
  updated_at?: string;
  is_read?: boolean;
  created_at?: string;
}

interface NewEmailForm {
  to: string[];
  to_input: string;
  subject: string;
  body: string;
  issue_id: string;
}

export default function EmailThreadsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const issueId = searchParams.get('issue_id');
  const threadIdParam = searchParams.get('thread');
  
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [newEmail, setNewEmail] = useState<NewEmailForm>({
    to: [],
    to_input: '',
    subject: '',
    body: '',
    issue_id: issueId || ''
  });
  
  const [convertForm, setConvertForm] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'normal',
    urgency_level: 2,
    reporter_name: '',
    reporter_email: '',
    reporter_phone: ''
  });

  const fetchThreads = useCallback(async (): Promise<EmailThread[]> => {
    try {
      const { data } = await getEmailThreads();
      // Client-side filter if issueId provided
      const filtered = issueId 
        ? data.filter((t: EmailThread) => t.issue_id === issueId)
        : data;
      setThreads(filtered);
      return filtered;
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
    return [];
  }, [issueId]);

  const fetchThread = useCallback(async (threadId: string) => {
    try {
      const { data } = await getEmailThread(threadId);
      setSelectedThread(data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await fetchThreads();
      // If thread parameter is provided, open that thread directly
      if (threadIdParam) {
        fetchThread(threadIdParam);
      }
      // Fetch organization for email from_name
      if (user?.organization_id) {
        try {
          const orgRes = await getOrganization(user.organization_id);
          setOrganization(orgRes.data || orgRes as unknown as Organization || null);
        } catch (e) {
          console.log('Could not fetch organization');
        }
      }
    };
    init();
  }, [issueId, threadIdParam, fetchThreads, fetchThread, user?.organization_id]);

  const handleCreateThread = async () => {
    const recipients = newEmail.to_input.split(',').map(e => e.trim()).filter(e => e);
    if (recipients.length === 0 || !newEmail.subject || !newEmail.body) {
      toast.error('Fyll i alla fält');
      return;
    }
    
    setSending(true);
    
    try {
      await sendEmail(recipients[0], newEmail.subject, newEmail.body, { fromName: organization?.name || 'Förvaltningen' });
      toast.success('E-post skickad');
      setNewThreadOpen(false);
      setNewEmail({ to: [], to_input: '', subject: '', body: '', issue_id: issueId || '' });
      fetchThreads();
    } catch (error) {
      toast.error('Kunde inte skicka e-post');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    
    const lastInbound = [...(selectedThread.messages || [])].reverse().find(m => m.direction === 'inbound');
    const toEmail = lastInbound?.from_email || selectedThread.participants?.[0];
    
    if (!toEmail) {
      toast.error('Kunde inte hitta mottagare');
      return;
    }
    
    setSending(true);
    
    try {
      await sendEmail(toEmail, `Re: ${selectedThread.subject}`, replyText, { fromName: organization?.name || 'Förvaltningen' });
      toast.success('Svar skickat');
      setReplyText('');
      fetchThread(selectedThread.id);
      fetchThreads();
    } catch (error) {
      toast.error('Kunde inte skicka svar');
    } finally {
      setSending(false);
    }
  };

  const handleConvertToIssue = async () => {
    if (!selectedThread) return;
    
    setConverting(true);
    
    try {
      // Create issue from thread data
      const { data } = await createIssue({
        title: convertForm.title || selectedThread.subject,
        description: convertForm.description || selectedThread.messages?.[0]?.body || '',
        category: convertForm.category,
        urgency_level: convertForm.priority === 'high' ? 3 : convertForm.priority === 'medium' ? 2 : 1,
        reporter_email: convertForm.reporter_email,
        reporter_phone: convertForm.reporter_phone,
        email_thread_id: selectedThread.id
      });
      
      toast.success('Ärende skapat');
      setConvertDialogOpen(false);
      navigate(`/issues/${data.id}`);
    } catch (error) {
      toast.error('Kunde inte skapa ärende');
    } finally {
      setConverting(false);
    }
  };

  // Delete thread with double confirmation
  const handleDeleteThread = () => {
    if (!selectedThread) return;
    
    toast(
      <div className="flex flex-col gap-2">
        <span className="font-medium">Vill du ta bort detta meddelande?</span>
        <span className="text-sm text-slate-500">"{selectedThread.subject}"</span>
        <div className="flex gap-2 mt-1">
          <Button 
            size="sm" 
            variant="destructive" 
            className="h-7 text-xs"
            onClick={() => {
              toast.dismiss();
              confirmDeleteThread();
            }}
          >
            Ja, ta bort
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="h-7 text-xs"
            onClick={() => toast.dismiss()}
          >
            Avbryt
          </Button>
        </div>
      </div>,
      { duration: 10000 }
    );
  };

  const confirmDeleteThread = async () => {
    toast(
      <div className="flex flex-col gap-2">
        <span className="font-medium text-red-600">Bekräfta borttagning</span>
        <span className="text-sm text-slate-500">Denna åtgärd kan inte ångras.</span>
        <div className="flex gap-2 mt-1">
          <Button 
            size="sm" 
            variant="destructive" 
            className="h-7 text-xs"
            onClick={async () => {
              if (!selectedThread) return;
              toast.dismiss();
              try {
                await deleteEmailThread(selectedThread.id);
                toast.success('Meddelandet har tagits bort');
                setSelectedThread(null);
                fetchThreads();
              } catch (error) {
                toast.error('Kunde inte ta bort meddelandet');
              }
            }}
          >
            Bekräfta
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="h-7 text-xs"
            onClick={() => toast.dismiss()}
          >
            Avbryt
          </Button>
        </div>
      </div>,
      { duration: 10000 }
    );
  };

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just nu';
    if (diffMins < 60) return `${diffMins} min sedan`;
    if (diffHours < 24) return `${diffHours} tim sedan`;
    if (diffDays < 7) return `${diffDays} dagar sedan`;
    return date.toLocaleDateString('sv-SE');
  };

  return (
    <div className="p-4 md:p-6 h-[calc(100vh-64px)]" data-testid="email-threads-page">
      <div className="flex h-full gap-4">
        {/* Thread List */}
        <div className={`${selectedThread ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 flex-shrink-0`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-semibold text-slate-900">
              E-post
            </h1>
            <Dialog open={newThreadOpen} onOpenChange={setNewThreadOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-7 text-xs" data-testid="new-email-button">
                  <Plus size={14} className="mr-1" />
                  Ny
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-base">Ny e-postkonversation</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Till * (kommaseparerade)</Label>
                    <Input
                      type="text"
                      value={newEmail.to_input}
                      onChange={(e) => setNewEmail({ ...newEmail, to_input: e.target.value })}
                      placeholder="email@example.com, email2@example.com"
                      className="h-8 text-sm"
                      data-testid="new-email-to"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ämne *</Label>
                    <Input
                      value={newEmail.subject}
                      onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                      placeholder="Ämne"
                      className="h-8 text-sm"
                      data-testid="new-email-subject"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Meddelande *</Label>
                    <Textarea
                      value={newEmail.body}
                      onChange={(e) => setNewEmail({ ...newEmail, body: e.target.value })}
                      placeholder="Skriv ditt meddelande..."
                      rows={5}
                      className="text-sm"
                      data-testid="new-email-body"
                    />
                  </div>
                  <Button 
                    onClick={handleCreateThread} 
                    disabled={sending}
                    className="w-full h-8 text-sm"
                    data-testid="send-new-email-button"
                  >
                    <PaperPlaneRight size={14} className="mr-1" />
                    {sending ? 'Skickar...' : 'Skicka'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Sök..." className="pl-8 h-8 text-sm" />
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto space-y-1.5 scrollbar-thin">
            {loading ? (
              <div className="space-y-1.5">
                {[1,2,3].map(i => (
                  <div key={i} className="p-3 bg-white rounded border border-border animate-pulse">
                    <div className="h-3.5 w-3/4 bg-slate-200 rounded mb-1.5" />
                    <div className="h-3 w-1/2 bg-slate-200 rounded" />
                  </div>
                ))}
              </div>
            ) : threads.length === 0 ? (
              <div className="text-center py-8">
                <EnvelopeSimple size={36} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">Inga konversationer</p>
              </div>
            ) : (
              threads.map((thread) => (
                <button
                  key={thread.id}
                  onClick={() => fetchThread(thread.id)}
                  className={`w-full text-left p-3 rounded border transition-colors ${
                    selectedThread?.id === thread.id
                      ? 'bg-slate-100 border-slate-300'
                      : thread.is_read === false
                        ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                        : 'bg-white border-border hover:bg-slate-50'
                  }`}
                  data-testid={`thread-item-${thread.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      {!thread.is_read && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                      {thread.issue && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium flex-shrink-0">
                          {thread.issue.issue_number || 'Ärende'}
                        </span>
                      )}
                      <h3 className={`text-sm text-slate-900 truncate ${!thread.is_read ? 'font-semibold' : 'font-medium'}`}>
                        {thread.subject}
                      </h3>
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">
                      {formatDate(thread.last_message_at || thread.updated_at)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mb-1.5">
                    {thread.last_message_preview || 'Inget meddelande'}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span>{thread.message_count || 0} meddelanden</span>
                    {thread.participants && thread.participants.length > 0 && (
                      <>
                        <span>•</span>
                        <span className="truncate">{thread.participants[0]}</span>
                      </>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Message View */}
        {selectedThread ? (
          <div className="flex-1 flex flex-col bg-white rounded border border-border">
            {/* Thread header */}
            <div className="p-3 border-b border-border">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedThread(null)}
                  className="md:hidden p-1.5 -ml-1.5 text-slate-500 hover:text-slate-700"
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {selectedThread.issue ? (
                      <Link 
                        to={`/issues/${selectedThread.issue_id}`}
                        className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium hover:bg-blue-200 transition-colors"
                      >
                        {selectedThread.issue.issue_number} →
                      </Link>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {selectedThread.reference}
                      </span>
                    )}
                    <h2 className="text-sm font-medium text-slate-900 truncate">
                      {selectedThread.subject}
                    </h2>
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {selectedThread.participants?.join(', ')}
                  </p>
                </div>
                
                {/* Convert to Issue button - only show if not already linked */}
                {!selectedThread.issue_id && (
                  <Dialog open={convertDialogOpen} onOpenChange={(open) => {
                    setConvertDialogOpen(open);
                    if (open && selectedThread) {
                      // Auto-fill email from first inbound message or participants
                      let reporterEmail = '';
                      const firstInbound = selectedThread.messages?.find(m => m.direction === 'inbound');
                      if (firstInbound) {
                        reporterEmail = firstInbound.from_email || '';
                      } else if (selectedThread.participants && selectedThread.participants.length > 0) {
                        reporterEmail = selectedThread.participants[0] || '';
                      }
                      setConvertForm(prev => ({
                        ...prev,
                        reporter_email: reporterEmail,
                        reporter_name: '',
                        reporter_phone: ''
                      }));
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs flex-shrink-0"
                        data-testid="convert-to-issue-button"
                      >
                        <Ticket size={14} className="mr-1" />
                        Skapa ärende
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-base flex items-center gap-2">
                          <Ticket size={18} />
                          Skapa ärende från e-post
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 mt-3">
                        <div className="bg-slate-50 rounded p-2.5 text-xs">
                          <p className="font-medium text-slate-700 mb-1">E-postens ämne blir ärendetitel:</p>
                          <p className="text-slate-600">{selectedThread.subject}</p>
                        </div>

                        {/* Reporter info section */}
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                          <div className="flex items-center gap-2 text-blue-700">
                            <User size={14} />
                            <span className="text-xs font-medium">Ärendeägarens uppgifter</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Namn *</Label>
                              <Input
                                value={convertForm.reporter_name}
                                onChange={(e) => setConvertForm({...convertForm, reporter_name: e.target.value})}
                                placeholder="Förnamn Efternamn"
                                className="h-8 text-xs"
                                data-testid="convert-reporter-name"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Telefon</Label>
                              <Input
                                value={convertForm.reporter_phone}
                                onChange={(e) => setConvertForm({...convertForm, reporter_phone: e.target.value})}
                                placeholder="070-123 45 67"
                                className="h-8 text-xs"
                                data-testid="convert-reporter-phone"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1">
                              <EnvelopeSimple size={12} />
                              E-post (från konversationen)
                            </Label>
                            <Input
                              value={convertForm.reporter_email}
                              onChange={(e) => setConvertForm({...convertForm, reporter_email: e.target.value})}
                              placeholder="email@example.com"
                              className="h-8 text-xs bg-white"
                              data-testid="convert-reporter-email"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1">
                              <Tag size={12} />
                              Kategori
                            </Label>
                            <Select
                              value={convertForm.category}
                              onValueChange={(value) => setConvertForm({...convertForm, category: value})}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CATEGORIES.map(cat => (
                                  <SelectItem key={cat.value} value={cat.value} className="text-xs">
                                    {cat.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1">
                              <Warning size={12} />
                              Brådskande
                            </Label>
                            <Select
                              value={String(convertForm.urgency_level)}
                              onValueChange={(value) => setConvertForm({...convertForm, urgency_level: parseInt(value)})}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1" className="text-xs">1 - Låg</SelectItem>
                                <SelectItem value="2" className="text-xs">2 - Normal</SelectItem>
                                <SelectItem value="3" className="text-xs">3 - Medel</SelectItem>
                                <SelectItem value="4" className="text-xs">4 - Hög</SelectItem>
                                <SelectItem value="5" className="text-xs">5 - Akut</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <Button 
                          onClick={handleConvertToIssue}
                          disabled={converting || !convertForm.reporter_name}
                          className="w-full h-8 text-sm"
                          data-testid="confirm-convert-button"
                        >
                          <CheckCircle size={14} className="mr-1" />
                          {converting ? 'Skapar...' : 'Skapa ärende'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                
                {/* Delete Thread Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDeleteThread}
                  data-testid="delete-thread-button"
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin bg-slate-50">
              {selectedThread.messages?.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.id}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg p-3 ${
                      msg.direction === 'outbound'
                        ? 'bg-slate-800 text-white'
                        : 'bg-white text-slate-900 border border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <User size={12} className={msg.direction === 'outbound' ? 'text-slate-400' : 'text-slate-400'} />
                      <span className={`text-[10px] ${msg.direction === 'outbound' ? 'text-slate-400' : 'text-slate-500'}`}>
                        {msg.from_email}
                      </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">
                      {msg.body}
                    </div>
                    <div className={`flex items-center gap-1 mt-2 text-[10px] ${
                      msg.direction === 'outbound' ? 'text-slate-500' : 'text-slate-400'
                    }`}>
                      <Clock size={10} />
                      {new Date(msg.created_at).toLocaleString('sv-SE')}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply input */}
            <div className="p-3 border-t border-border bg-white">
              <div className="flex gap-2">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Skriv ett svar..."
                  rows={2}
                  className="flex-1 resize-none text-sm min-h-[60px]"
                  data-testid="reply-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleReply();
                    }
                  }}
                />
                <Button
                  onClick={handleReply}
                  disabled={!replyText.trim() || sending}
                  className="self-end h-8"
                  data-testid="send-reply-button"
                >
                  <PaperPlaneRight size={16} />
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">
                ⌘/Ctrl+Enter för att skicka
              </p>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-slate-50 rounded border border-border">
            <div className="text-center">
              <EnvelopeSimple size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Välj en konversation</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
